import React, { useCallback, useEffect, useState } from 'react';

import { Alert, App, Button, ConfigProvider, GetProps, Input, Radio, Skeleton, Space, Typography } from 'antd';

import { StarOutlined, SyncOutlined } from '@ant-design/icons';
import { createIssueCompletionAiTextSuggestions } from 'services/Issue.service';
import { ClientManager } from 'types/ClientManager.type';

import Authentication from './Authentication';
import { handleServiceErrorV2, hasServiceErrorV2 } from './helpers/ServiceHelper';

type TextAreaProps = GetProps<typeof Input.TextArea>;

interface Props extends TextAreaProps {
    modelId: number,
    service: () => ReturnType<typeof createIssueCompletionAiTextSuggestions>;
}

type Feedback = {
    id: number,
    user: Pick<User.Model, 'id' | 'name'>,
    clientManagerName: ClientManager.Model['name'],
    suggestion: string,
}

const sendUsageFeedbackToN8n = async ({ id, user, clientManagerName, suggestion }: Feedback) => {
    fetch('https://gcondo.app.n8n.cloud/webhook/4d2ab71e-d6c7-45ba-b41e-a59a6e5b377b', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        mode: 'no-cors',
        body: JSON.stringify({
            id,
            user,
            clientManagerName,
            suggestion,
            model: 'issue',
            action: 'finish'
        })
    }).catch(() => { });
};

// TODO(309): Review this all component again....
export function CompletionAiTextAreaSuggestions({ modelId, service, ...restProps }: Props) {
    const [isFetching, setIsFetching] = useState(true);
    const [suggestions, setSuggestions] = useState<string[]>([]);

    const app = App.useApp();

    const fetchSuggestions = useCallback(async () => {
        setIsFetching(true);

        const response = await service();

        if (hasServiceErrorV2(response))
            return handleServiceErrorV2(app, response);

        setSuggestions(response.data.suggestions);

        setIsFetching(false);
    }, [app, service]);

    useEffect(() => {
        fetchSuggestions();
    }, [fetchSuggestions]);

    const message = (
        <Space align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
            <Space>
                <StarOutlined size={16} style={{ color: 'var(--ant-purple-6)' }} />

                <Typography.Text style={{ color: 'purple' }} strong>
                    Sugestões com Gcondo.ai
                </Typography.Text>
            </Space>

            <Button
                icon={<SyncOutlined />}
                size="small"
                color="purple"
                variant="filled"
                onClick={fetchSuggestions}
                loading={isFetching}
            >
                Gerar novamente
            </Button>
        </Space>

    );

    const description = isFetching ? <Skeleton title={false} /> : (
        <Space direction="vertical">
            <ConfigProvider
                theme={{
                    components: {
                        Radio: {
                            colorBgBase: 'var(--ant-purple-1)',
                            colorBorder: 'var(--ant-purple-3)',
                            colorPrimary: 'var(--ant-purple-6)',
                            colorPrimaryActive: 'var(--ant-purple-6)',
                            colorPrimaryHover: 'var(--ant-purple-6)',
                        }
                    }
                }}>
                <Radio.Group
                    onChange={(e) => {
                        restProps.onChange?.(e.target.value);

                        const user = {
                            id: Authentication.getUserId(),
                            name: Authentication.getUserName(),
                        };

                        const clientManagerName = Authentication.getClientManager().name;

                        sendUsageFeedbackToN8n({ id: modelId, user, clientManagerName, suggestion: e.target.value });
                    }}
                    value={restProps.value}
                >
                    {suggestions.length > 0 ? suggestions.map((suggestion, index) => (
                        <Radio
                            key={index}
                            value={suggestion}
                            style={{
                                borderRadius: 4,
                                marginBottom: 8,
                                padding: 5,
                                border: '1px solid var(--ant-purple-3)',
                                fontSize: 12,
                                width: '100%',
                            }}
                        >
                            {suggestion}
                        </Radio>
                    )
                    ) : 'Não foi possível gerar sugestões.'}
                </Radio.Group>
            </ConfigProvider>
        </Space>
    );

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <Alert
                showIcon={false}
                message={message}
                description={description}
                style={{
                    padding: '8px 12px',
                    backgroundColor: 'var(--ant-purple-1)',
                    borderColor: 'var(--ant-purple-3)',
                    fontSize: 12,
                }}
            />

            <Input.TextArea {...restProps} />
        </Space>
    );
}
